import { takeLatest, call, select, put } from 'redux-saga/effects';
import { get } from '@airasia-common/libraries/http/hotelRequest';
import ssoClient from '@airasia-common/libraries/http/ssoClient';
import { toHotelAPILanguage } from '../../hotel/ApiParams';
import genericApiSaga, { genericMultipleApiSaga } from '../genericApiSaga';
import { actionCreators, types } from '../../redux/booking/booking.meta';
import { actionCreators as analyticsActionCreators } from '../../redux/analytics/analytics.meta';
import {
  LOB_HOTEL,
  BOOKING_CONFIRMATION_PAGE,
} from '../../redux/analytics/analytics.constants';
import { getPrecancellationData, cancelBooking } from './booking.gateway';

export function* fetchBooking({ payload }) {
  const requestStart = new Date().getTime();
  const ssoOrigin = yield ssoClient.authorize();
  const { clientId, apiKey } = ssoOrigin;
  function* onResponse(response) {
    if (response.status) {
      let newResponse = response.body;
      yield put(
        actionCreators.fetchBookingCompleted({
          ...newResponse,
          ssoOriginData: ssoOrigin,
        })
      );
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'Booking Confirmation',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: BOOKING_CONFIRMATION_PAGE,
        })
      );
    }
  }

  yield call(genericApiSaga, {
    onResponse: onResponse,
    completed: actionCreators.fetchBookingCompleted,
    failed: actionCreators.fetchBookingFailed,
    gatewayCall: () =>
      get(`/booking/v1/hotels/booking/wrapper/order/${payload}`),
  });
}

export function* fetchBookingHotelContent(action) {
  const { hotel_id: hotelId, rooms } = action.payload;
  const [room] = rooms || [];
  const { room_id: roomId } = room || {};
  const { languageId } = yield select(state => state.filter.hotel);

  yield genericMultipleApiSaga({
    completed: actionCreators.fetchBookingHotelContentCompleted,
    failed: actionCreators.fetchBookingHotelContentFailed,
    gatewayCalls: [
      () =>
        get(`/details/v2/hotels/${hotelId}/content`, {
          language: toHotelAPILanguage(languageId),
        }),
      () =>
        get(`/details/v2/hotels/${hotelId}/room/${roomId}/content`, {
          language: toHotelAPILanguage(languageId),
        }),
    ],
  });
}

export function* requestPrecancellation({ payload: preCancelHref }) {
  const requestStart = new Date().getTime();
  function* onResponse(response) {
    if (response.status) {
      const responseEnd = new Date().getTime();
      yield put(
        analyticsActionCreators.trackReceiveApiResponse({
          api: 'Precancellation',
          responseTime: responseEnd - requestStart,
          lob: LOB_HOTEL,
          pageId: BOOKING_CONFIRMATION_PAGE,
        })
      );
    }
  }

  yield call(genericApiSaga, {
    onResponse: onResponse,
    completed: actionCreators.requestPrecancellationCompleted,
    failed: actionCreators.requestPrecancellationFailed,
    gatewayCall: getPrecancellationData(preCancelHref),
  });
}

export function* requestCancellation({ payload }) {
  const { cancelHref, reason, refund } = payload;
  const body = { reason, refund };

  yield call(genericApiSaga, {
    completed: actionCreators.requestCancellationCompleted,
    failed: actionCreators.requestCancellationFailed,
    gatewayCall: cancelBooking(cancelHref, body),
  });
}

export function* reloadBooking() {
  const { key } = yield select(state => state.booking);

  yield put(actionCreators.reloadBooking(key));
}

export default function* createBookingSaga() {
  yield takeLatest(types.RELOAD_BOOKING, fetchBooking);
  yield takeLatest(types.FETCH_BOOKING, fetchBooking);
  yield takeLatest(types.FETCH_BOOKING_COMPLETED, fetchBookingHotelContent);
  yield takeLatest(types.PRECANCELLATION_REQUEST, requestPrecancellation);
  yield takeLatest(types.CANCELLATION_REQUEST, requestCancellation);
  yield takeLatest(types.CANCELLATION_REQUEST_COMPLETED, reloadBooking);
}
